/**
 * Sidebar persistente do PharuERP.
 *
 * Estrutura visual em 3 zonas verticais:
 *   - Header (apenas botao de colapsar/fechar, sem logo)
 *   - ScrollArea com accordions top-level (uma por categoria)
 *   - Footer com tag de unidade (so quando expandido)
 *
 * Estados:
 *   - expandido (248px): cada categoria top-level eh um accordion. Click no
 *     header expande/recolhe. Estado persistido em localStorage por moduloId
 *     (hook useTopLevelAccordion).
 *   - colapsado (64px): mini-rail. Cada categoria vira UM icone. Hover/click
 *     abre flyout a direita via Portal com label + grupos + itens.
 *   - mobile (<=920px): drawer overlay sempre em expandido.
 *
 * Preferencia de colapsado persiste via useSidebarPreference.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import {
  FiBox,
  FiChevronRight,
  FiChevronsLeft,
  FiExternalLink,
  FiStar,
  FiX,
} from "react-icons/fi";
import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import useDisposicaoPendentes from "../../hooks/useDisposicaoPendentes";
import useFavoritos from "../../hooks/useFavoritos";
import useAjustesAguardando from "../../hooks/useAjustesAguardando";
import useNotificacoesAgregadas from "../../hooks/useNotificacoesAgregadas";
import { useWhatsappNaoLidasGlobal } from "../../hooks/useWhatsappNaoLidas";
import useEstadoCentral from "../../hooks/useEstadoCentral";
import { moduloDaRota, itensDoGrupo, modulosVisiveis as filtrarModulosDoMenu } from "../../utils/modules";
import { useTopLevelAccordion } from "../../hooks/useTopLevelAccordion";
import {
  CollapseButton,
  FavoritesHeader,
  FavoritesSection,
  FlyoutGroup,
  FlyoutGroupLabel,
  FlyoutHeader,
  FlyoutItem,
  GroupAccordion,
  GroupHeader,
  GroupItems,
  SubGroupHeader,
  SubGroupItems,
  NavItemFavoritoRow,
  RailFlyout,
  RailIconButton,
  RailItemWrap,
  RailSectionDivider,
  ScrollArea,
  SidebarBadge,
  SidebarDrawer,
  SidebarFooter,
  SidebarHeader,
  SidebarNavLink,
  SidebarOverlay,
  SidebarRoot,
  StarButton,
  TopLevelAccordion,
  TopLevelContent,
  TopLevelHeader,
  Tooltip,
  UnitTag,
} from "./styles";

/**
 * Resolve o valor do badge (string ou null) baseado no modulo.badge key.
 * Hoje so suporta "naoLidasWhatsapp". Quando aparecer mais um (ex.: NF-e
 * pendente), so adicionar um case aqui.
 */
function useBadgeValor(modulo) {
  const naoLidas = useWhatsappNaoLidasGlobal();
  if (!modulo?.badge) return null;
  if (modulo.badge === "naoLidasWhatsapp") {
    const total = naoLidas?.total || 0;
    if (total <= 0) return null;
    return total > 99 ? "99+" : String(total);
  }
  return null;
}

/**
 * Badge por ITEM da sidebar (Lote D): mesmo contrato do useBadgeValor,
 * mas resolvido pela key `badge` do item do catalogo (utils/modules.jsx).
 * Hoje so "disposicaoPendentes" (fila de disposicao de pecas do PCP) —
 * novo caso = novo case aqui.
 */
function useItemBadgeValor(item) {
  const disposicao = useDisposicaoPendentes({
    enabled: item?.badge === "disposicaoPendentes",
  });
  // Bloco 4 (12/09/2026): ajustes de estoque esperando aprovacao, so para
  // quem pode aprovar (o hook ja gateia por estoque:aprovar).
  const ajustes = useAjustesAguardando({
    enabled: item?.badge === "ajustesAguardando",
  });
  // Chat Pharu (ADR-0077): não lidas já contadas pelo sino (mesmo contexto,
  // sem polling a mais).
  const { fontes } = useNotificacoesAgregadas();
  if (!item?.badge) return null;
  if (item.badge === "chatNaoLidas") {
    const chat = (fontes || []).find((f) => f.id === "chat");
    const n = chat?.count || 0;
    if (n <= 0) return null;
    return n > 99 ? "99+" : String(n);
  }
  if (item.badge === "disposicaoPendentes") {
    if (disposicao <= 0) return null;
    return disposicao > 99 ? "99+" : String(disposicao);
  }
  if (item.badge === "ajustesAguardando") {
    if (ajustes <= 0) return null;
    return ajustes > 99 ? "99+" : String(ajustes);
  }
  return null;
}

// Bugfix 2026-05-28 (#11 retrabalho): aumentado de 220ms para 450ms.
// 220ms eh muito pouco quando o usuario move o mouse lentamente do
// icone ate o flyout — o cursor sai do RailItemWrap (dispara
// scheduleClose), atravessa o gap, e o close dispara antes do mouse
// chegar no flyout (que cancelaria). Com 450ms ha folga suficiente
// para qualquer movimento humano razoavel.
const FLYOUT_CLOSE_DELAY_MS = 450;

function defaultIconFor() {
  return FiBox;
}

function isItemAtivo(item, pathname) {
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * Leaf detection: categoria com 1 grupo sem label e 1 item so (ex.: Visao
 * Geral). No rail colapsado, leaves nao usam flyout — viram link direto.
 */
function isLeafModule(modulo) {
  return (
    modulo.grupos.length === 1 &&
    !modulo.grupos[0].label &&
    modulo.grupos[0].itens.length === 1
  );
}

function NavItem({ item, collapsed, nested, onNavigate, favoritavel = true }) {
  const Icone = item.icone || defaultIconFor(item);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const { isFavorito, toggle: toggleFavorito } = useFavoritos();
  const badgeValor = useItemBadgeValor(item);

  // UI.5: somente em modo expandido + item tem pageId (chave estavel) +
  // explicitamente favoritavel (default true). Itens sem pageId nao podem
  // ser favoritados — seria impossivel encontrar/restaurar.
  const pageKey = item.pageId || item.to;
  const canFavoritar =
    !collapsed && favoritavel && pageKey;
  const ativo = canFavoritar && isFavorito(pageKey);

  const conteudo = (
    <>
      <span className="icon">
        <Icone size={16} aria-hidden="true" />
      </span>
      {!collapsed && <span className="label">{item.label}</span>}
      {!collapsed && item.href && (
        <FiExternalLink size={12} aria-hidden="true" style={{ opacity: 0.6 }} />
      )}
      {!collapsed && badgeValor && <SidebarBadge>{badgeValor}</SidebarBadge>}
      {collapsed && tooltipVisible && <Tooltip role="tooltip">{item.label}</Tooltip>}
    </>
  );

  // Item com `href` aponta para OUTRO produto Pharu, em outro dominio.
  // Precisa virar `<a>`: `NavLink to="https://..."` seria interpretado
  // como rota interna e o clique levaria a uma pagina em branco.
  const link = item.href ? (
    <SidebarNavLink
      as="a"
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      $nested={nested}
      onMouseEnter={() => collapsed && setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
    >
      {conteudo}
    </SidebarNavLink>
  ) : (
    <SidebarNavLink
      to={item.to}
      onClick={onNavigate}
      data-tour={`menu:${item.to}`}
      $nested={nested}
      onMouseEnter={() => collapsed && setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
      end={item.exact}
    >
      {conteudo}
    </SidebarNavLink>
  );

  if (!canFavoritar) return link;

  return (
    <NavItemFavoritoRow>
      {link}
      <StarButton
        type="button"
        $ativo={ativo}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleFavorito(pageKey);
        }}
        aria-label={ativo ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        title={ativo ? "Remover dos favoritos" : "Marcar como favorito"}
      >
        <FiStar
          size={13}
          fill={ativo ? "currentColor" : "none"}
          strokeWidth={2}
        />
      </StarButton>
    </NavItemFavoritoRow>
  );
}

/**
 * UI.5 (Bloco UI, auditoria 2026-05-23): seção Favoritos no topo da
 * sidebar (so em modo expandido, so se ha pelo menos 1 favorito visivel
 * para o usuario). Lista os items favoritados resolvendo de cada modulo
 * visivel (que ja foi filtrado por permissao).
 */
function FavoritosSecao({ modulosVisiveis, onNavigate }) {
  const { favoritos } = useFavoritos();
  const [aberto, setAberto] = useState(true);

  // Achata items de todos os modulos visiveis e pega so os favoritados.
  // Preserva ordem natural da sidebar (modulo + grupo + item).
  const itensFavoritos = useMemo(() => {
    const acc = [];
    modulosVisiveis.forEach((modulo) => {
      modulo.grupos.forEach((grupo) => {
        itensDoGrupo(grupo).forEach((item) => {
          const key = item.pageId || item.to;
          if (key && favoritos.has(key)) {
            acc.push(item);
          }
        });
      });
    });
    return acc;
  }, [modulosVisiveis, favoritos]);

  if (itensFavoritos.length === 0) return null;

  return (
    <FavoritesSection>
      <FavoritesHeader
        type="button"
        data-open={aberto}
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
      >
        <span className="icon">
          <FiStar size={12} fill="currentColor" strokeWidth={2} />
        </span>
        <span className="label">Favoritos</span>
        <span className="chevron" aria-hidden="true">
          <FiChevronRight size={12} />
        </span>
      </FavoritesHeader>
      <AnimatePresence initial={false}>
        {aberto && (
          <GroupItems
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            {itensFavoritos.map((item) => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={false}
                nested
                onNavigate={onNavigate}
                /* Mantem botao estrela disponivel para des-favoritar daqui */
              />
            ))}
          </GroupItems>
        )}
      </AnimatePresence>
    </FavoritesSection>
  );
}

/**
 * Grupo intermediario DENTRO de uma categoria top-level expandida.
 * Tem accordion proprio (collapsable). So aparece em modo expandido — no
 * colapsado o flyout cuida da renderizacao via portal.
 */
/**
 * Sub-grupo dentro de um grupo (nivel novo, 2026-08-04): "Projetos
 * Executivos" > Análises / Painéis / Base. Cada um e um accordion proprio,
 * texto simples indentado (sem icone, como no mockup do Igor), que abre as
 * paginas ao clicar. Abre sozinho quando a rota atual esta dentro dele.
 */
function SubGroup({ subgrupo, onNavigate }) {
  const { pathname } = useLocation();
  const ativo = subgrupo.itens.some((item) => isItemAtivo(item, pathname));
  const [open, setOpen] = useState(ativo);

  return (
    <div>
      <SubGroupHeader
        type="button"
        data-open={open}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="label">{subgrupo.label}</span>
        <span className="chevron" aria-hidden="true">
          <FiChevronRight size={11} />
        </span>
      </SubGroupHeader>
      <AnimatePresence initial={false}>
        {open && (
          <SubGroupItems
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            {subgrupo.itens.map((item) => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={false}
                nested
                onNavigate={onNavigate}
              />
            ))}
          </SubGroupItems>
        )}
      </AnimatePresence>
    </div>
  );
}

function Group({ grupo, defaultOpen, onNavigate }) {
  const [open, setOpen] = useState(defaultOpen);

  // Grupo sem label: renderiza itens diretos (ex.: Comercial > 1 grupo so).
  if (!grupo.label) {
    return (
      <GroupAccordion>
        {grupo.itens.map((item) => (
          <NavItem
            key={item.to}
            item={item}
            collapsed={false}
            nested={false}
            onNavigate={onNavigate}
          />
        ))}
      </GroupAccordion>
    );
  }

  // Redesenho 2026-08-04: grupo com `icone` renderiza como ITEM (icone +
  // texto normal, mesmo visual de Inicio/Clientes) em vez do cabecalho em
  // caixa alta — pedido do dono do produto para a sidebar do Legado. O
  // atributo data-item liga a variante no styled-component.
  const Icone = grupo.icone || null;

  return (
    <GroupAccordion>
      <GroupHeader
        type="button"
        data-open={open}
        data-item={Icone ? "true" : undefined}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {Icone && (
          <span className="icone" aria-hidden="true">
            <Icone size={15} />
          </span>
        )}
        <span className="label">{grupo.label}</span>
        <span className="chevron" aria-hidden="true">
          <FiChevronRight size={12} />
        </span>
      </GroupHeader>
      <AnimatePresence initial={false}>
        {open && (
          <GroupItems
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            {grupo.itens.map((item) => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={false}
                nested
                onNavigate={onNavigate}
              />
            ))}
            {(grupo.subgrupos || []).map((sg) => (
              <SubGroup key={sg.label} subgrupo={sg} onNavigate={onNavigate} />
            ))}
          </GroupItems>
        )}
      </AnimatePresence>
    </GroupAccordion>
  );
}

/**
 * Categoria top-level no estado EXPANDIDO. Accordion clicavel com chevron.
 *
 * Casos:
 *  - modulo tem 1 grupo sem label E so 1 item ("leaf" tipo Visao Geral):
 *    renderiza como link direto (sem accordion, sem chevron). Item se torna
 *    o proprio header.
 *  - todos os outros: header com chevron, conteudo abre/recolhe.
 */
function TopLevelSecao({ modulo, pathname, abertoState, toggle, onNavigate }) {
  const Icone = modulo.icone || FiBox;
  const badgeValor = useBadgeValor(modulo);
  const moduloAtivo = useMemo(
    () =>
      modulo.grupos.some((g) =>
        itensDoGrupo(g).some((item) => isItemAtivo(item, pathname))
      ),
    [modulo, pathname]
  );

  // Caso especial: leaf direta (1 grupo, sem label, 1 item) — sem accordion.
  if (
    modulo.grupos.length === 1 &&
    !modulo.grupos[0].label &&
    modulo.grupos[0].itens.length === 1
  ) {
    const item = modulo.grupos[0].itens[0];
    return (
      <NavItem
        item={{ ...item, icone: modulo.icone || item.icone, label: modulo.label }}
        collapsed={false}
        nested={false}
        onNavigate={onNavigate}
      />
    );
  }

  const aberto = abertoState[modulo.id] || false;

  return (
    <TopLevelAccordion>
      <TopLevelHeader
        type="button"
        data-tour={`modulo:${modulo.id}`}
        data-open={aberto}
        $active={moduloAtivo}
        onClick={() => toggle(modulo.id)}
        aria-expanded={aberto}
      >
        <span className="icon">
          <Icone size={16} aria-hidden="true" />
        </span>
        <span className="label">{modulo.label}</span>
        {badgeValor && <SidebarBadge>{badgeValor}</SidebarBadge>}
        <span className="chevron" aria-hidden="true">
          <FiChevronRight size={14} />
        </span>
      </TopLevelHeader>
      <AnimatePresence initial={false}>
        {aberto && (
          <TopLevelContent
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {modulo.grupos.map((grupo, idx) => {
              const grupoAtivo = itensDoGrupo(grupo).some((item) =>
                isItemAtivo(item, pathname)
              );
              return (
                <Group
                  key={grupo.label || `g-${idx}`}
                  grupo={grupo}
                  defaultOpen={grupoAtivo || !grupo.label}
                  onNavigate={onNavigate}
                />
              );
            })}
          </TopLevelContent>
        )}
      </AnimatePresence>
    </TopLevelAccordion>
  );
}

/**
 * Categoria top-level no estado COLAPSADO (rail). Um icone clicavel + flyout
 * via Portal. Mesma logica que ja existia para PCP, agora aplicada a TODAS
 * as categorias (`moduleNeedsFlyout` retorna true sempre).
 */
// Bugfix 2026-05-28 (#11 retrabalho): de 10 para -2 (overlap 2px no icone).
// Antes havia gap de 10px entre o icone e o flyout — no movimento lento
// do mouse, sair do icone para o flyout passava por uma "zona morta" sem
// hover, e o scheduleClose disparava antes do mouse chegar no destino.
// Com overlap, hover continuo: a saida do icone JA eh entrada no flyout.
// A seta visual (::before/::after) ainda funciona pois o flyout tem sua
// borda esquerda visivel; soh nao ha mais gap apos ela.
const FLYOUT_OFFSET_X = -2;
const FLYOUT_VIEWPORT_PADDING = 12;
const ESTIMATED_FLYOUT_WIDTH = 280;

/**
 * Variante do mini-rail para LEAFS (categoria com 1 item so). Renderiza um
 * NavLink direto envolvendo o icone — clicar navega imediatamente, sem
 * flyout. Hover mostra tooltip nativo (title).
 */
function ModuleRailLeaf({ modulo, pathname, onNavigate }) {
  const item = modulo.grupos[0].itens[0];
  const Icone = modulo.icone || item.icone || FiBox;
  const ativo = isItemAtivo(item, pathname);
  const icone = (
    <RailIconButton
      as="span"
      $active={ativo}
      title={modulo.label}
      aria-label={modulo.label}
    >
      <Icone size={18} aria-hidden="true" />
    </RailIconButton>
  );
  // `NavLink` do react-router NAO aceita `as` — para link de outro
  // produto Pharu tem que ser âncora de verdade.
  return (
    <RailItemWrap data-tour={`modulo:${modulo.id}`}>
      {item.href ? (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          style={{ textDecoration: "none" }}
        >
          {icone}
        </a>
      ) : (
        <NavLink
          to={item.to}
          onClick={onNavigate}
          end={item.exact}
          style={{ textDecoration: "none" }}
        >
          {icone}
        </NavLink>
      )}
    </RailItemWrap>
  );
}

/**
 * Item do flyout do rail colapsado — componente proprio para poder
 * resolver o badge por item via hook (hooks nao rodam dentro de .map).
 */
function FlyoutNavItem({ item, onNavigate }) {
  const ItemIcon = item.icone || defaultIconFor(item);
  const badgeValor = useItemBadgeValor(item);
  // Mesmo motivo do `NavItem`: item de outro produto Pharu vive em outro
  // dominio e precisa de `<a>`, senao o router trata a URL como rota.
  const externo = Boolean(item.href);
  return (
    <FlyoutItem
      {...(externo
        ? { as: "a", href: item.href, target: "_blank", rel: "noopener noreferrer" }
        : { to: item.to, end: item.exact })}
      onClick={onNavigate}
    >
      <span className="icon">
        <ItemIcon size={14} aria-hidden="true" />
      </span>
      {item.label}
      {externo && <FiExternalLink size={11} aria-hidden="true" style={{ opacity: 0.6 }} />}
      {badgeValor && <SidebarBadge>{badgeValor}</SidebarBadge>}
    </FlyoutItem>
  );
}

function ModuleRailItem({ modulo, pathname, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const closeTimerRef = useRef(null);
  const iconButtonRef = useRef(null);
  const flyoutRef = useRef(null);

  // Bugfix 2026-05-29 (#11 fix v4): coordenacao entre RailItems via
  // CustomEvent. Quando um item abre, dispatch "sidebar-rail-open" com
  // seu proprio id. Outros itens escutam e fecham IMEDIATAMENTE
  // (sem delay do scheduleClose). Antes, sem coordenacao, mover mouse
  // de Legado pra Financeiro mantinha Legado visivel por 450ms enquanto
  // Financeiro ja tinha aberto — varios submenus empilhados na tela.
  const idRef = useRef(modulo.label || modulo.id || Math.random().toString(36));

  const Icone = modulo.icone || FiBox;
  const moduloAtivo = useMemo(
    () =>
      modulo.grupos.some((g) =>
        itensDoGrupo(g).some((item) => isItemAtivo(item, pathname))
      ),
    [modulo, pathname]
  );

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Bugfix 2026-05-29 (#11 fix v3 apos debug visual in vivo):
  // safe-zone com posicao real do mouse. As 2 tentativas anteriores
  // (RailFlyoutHoverBridge + overlap FLYOUT_OFFSET_X=-2 + delay maior)
  // ainda perdiam o hover em casos edge. Esta versao consulta a posicao
  // REAL do mouse antes de fechar — se o cursor estiver dentro da safe
  // zone (bounding box que une icone + gap + flyout), reagenda em vez
  // de fechar. So fecha quando o usuario REALMENTE saiu da area.
  const isMouseDentroSafeZone = () => {
    const btn = iconButtonRef.current;
    const flyout = flyoutRef.current;
    const mp = window.__sidebarMousePos;
    if (!btn || !mp || mp.x == null) return false;
    const iconRect = btn.getBoundingClientRect();
    // Safe zone = bounding box que cobre icone + gap + flyout, com folga
    // de 8px em todos os lados para tolerar tremor do mouse.
    const PAD = 8;
    let left = iconRect.left - PAD;
    let right = iconRect.right + PAD;
    let top = iconRect.top - PAD;
    let bottom = iconRect.bottom + PAD;
    if (flyout) {
      const fr = flyout.getBoundingClientRect();
      left = Math.min(left, fr.left - PAD);
      right = Math.max(right, fr.right + PAD);
      top = Math.min(top, fr.top - PAD);
      bottom = Math.max(bottom, fr.bottom + PAD);
    }
    return mp.x >= left && mp.x <= right && mp.y >= top && mp.y <= bottom;
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      // Reconsulta antes de fechar: se mouse ainda esta dentro da safe
      // zone (icone + gap + flyout), reagenda. Evita fechar prematuro
      // quando o mouse esta atravessando o gap lentamente.
      if (isMouseDentroSafeZone()) {
        scheduleClose();
        return;
      }
      setOpen(false);
    }, FLYOUT_CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), []);

  function handleNavigate() {
    setOpen(false);
    onNavigate?.();
  }

  const recalcularPosicao = () => {
    const btn = iconButtonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const flyoutH = flyoutRef.current?.offsetHeight || 0;
    let top = rect.top - 4;
    if (flyoutH > 0) {
      const maxTop = window.innerHeight - flyoutH - FLYOUT_VIEWPORT_PADDING;
      top = Math.min(
        Math.max(FLYOUT_VIEWPORT_PADDING, top),
        Math.max(FLYOUT_VIEWPORT_PADDING, maxTop)
      );
    }
    let left = rect.right + FLYOUT_OFFSET_X;
    if (left + ESTIMATED_FLYOUT_WIDTH > window.innerWidth - FLYOUT_VIEWPORT_PADDING) {
      left = Math.max(
        FLYOUT_VIEWPORT_PADDING,
        rect.left - ESTIMATED_FLYOUT_WIDTH - FLYOUT_OFFSET_X
      );
    }
    setPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (open) recalcularPosicao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onResize = () => recalcularPosicao();
    const onScroll = () => recalcularPosicao();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onMouseDown = (e) => {
      const target = e.target;
      if (
        iconButtonRef.current?.contains(target) ||
        flyoutRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    // Quando outro RailItem abre, este fecha imediatamente (sem delay).
    const onOutroAbriu = (e) => {
      if (e.detail?.id !== idRef.current) {
        cancelClose();
        setOpen(false);
      }
    };

    // Notifica os outros que este aqui acabou de abrir.
    window.dispatchEvent(
      new CustomEvent("sidebar-rail-open", { detail: { id: idRef.current } }),
    );

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("sidebar-rail-open", onOutroAbriu);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("sidebar-rail-open", onOutroAbriu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // HF-D5 v3: AnimatePresence em volta de createPortal NAO funciona —
  // AnimatePresence nao consegue rastrear o tipo especial Portal sem `key`
  // e o flyout silenciosamente nao monta no DOM mesmo com `open === true`
  // e a position calculada. Solucao: portal renderizado direto (sem
  // AnimatePresence). Sacrificamos a animacao de exit, mas o RailFlyout
  // ainda anima entrada via initial/animate do motion.div. UX continua boa.
  const flyout =
    open &&
    createPortal(
      <RailFlyout
        ref={flyoutRef}
        style={{ top: position.top, left: position.left }}
        initial={{ opacity: 0, x: -4 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.14 }}
        role="menu"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <FlyoutHeader>{modulo.label}</FlyoutHeader>
        {modulo.grupos.flatMap((grupo, idx) => {
          const blocos = [];
          // Grupo pai so entra se tiver itens proprios (Projetos Executivos
          // tem itens: [] — os dele vivem nos subgrupos abaixo).
          if ((grupo.itens || []).length > 0) {
            blocos.push(
              <FlyoutGroup key={grupo.label || `g-${idx}`}>
                {grupo.label && <FlyoutGroupLabel>{grupo.label}</FlyoutGroupLabel>}
                {grupo.itens.map((item) => (
                  <FlyoutNavItem
                    key={item.to}
                    item={item}
                    onNavigate={handleNavigate}
                  />
                ))}
              </FlyoutGroup>,
            );
          }
          (grupo.subgrupos || []).forEach((sg) => {
            blocos.push(
              <FlyoutGroup key={`${grupo.label} · ${sg.label}`}>
                <FlyoutGroupLabel>{`${grupo.label} · ${sg.label}`}</FlyoutGroupLabel>
                {sg.itens.map((item) => (
                  <FlyoutNavItem
                    key={item.to}
                    item={item}
                    onNavigate={handleNavigate}
                  />
                ))}
              </FlyoutGroup>,
            );
          });
          return blocos;
        })}
      </RailFlyout>,
      document.body
    );

  return (
    <RailItemWrap
      data-tour={`modulo:${modulo.id}`}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <RailIconButton
        ref={iconButtonRef}
        type="button"
        $active={moduloAtivo}
        onClick={() => setOpen(true)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={modulo.label}
      >
        <Icone size={18} aria-hidden="true" />
      </RailIconButton>
      {flyout}
    </RailItemWrap>
  );
}

function SidebarContent({
  collapsed,
  onToggleCollapsed,
  onClose,
  modulosVisiveis,
  pathname,
  isMobile,
  canToggle = true,
  abertoState,
  toggleTopLevel,
}) {
  const { usuario } = useAuth();
  const unidadeDaSessao = [usuario?.business_unit_codigo, usuario?.business_unit_nome].filter(Boolean).join(" ");
  return (
    <>
      {/* Header da sidebar: botão de fechar (mobile) ou colapsar (desktop).
          Na faixa tablet (1024-1280) o rail é forçado e o botão de colapsar
          some (canToggle=false) — não há ganho em alternar nessa largura. */}
      {(isMobile || canToggle) && (
        <SidebarHeader>
          {isMobile ? (
            <CollapseButton
              type="button"
              onClick={onClose}
              aria-label="Fechar menu"
            >
              <FiX size={18} />
            </CollapseButton>
          ) : (
            <CollapseButton
              type="button"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? "Expandir menu" : "Colapsar menu"}
              title={collapsed ? "Expandir" : "Colapsar"}
            >
              {collapsed ? <FiChevronRight size={16} /> : <FiChevronsLeft size={16} />}
            </CollapseButton>
          )}
        </SidebarHeader>
      )}

      <ScrollArea>
        {/* UI.5 (Bloco UI): seção Favoritos no topo, só em modo expandido.
            Se vazia ou colapsada, FavoritosSecao retorna null automaticamente. */}
        {!collapsed && (
          <FavoritosSecao
            modulosVisiveis={modulosVisiveis}
            onNavigate={onClose}
          />
        )}

        {modulosVisiveis.map((modulo, idx) => (
          <React.Fragment key={modulo.id}>
            {/* Divisor entre categorias no rail colapsado */}
            {idx > 0 && <RailSectionDivider $collapsed={collapsed} />}
            {collapsed ? (
              isLeafModule(modulo) ? (
                <ModuleRailLeaf
                  modulo={modulo}
                  pathname={pathname}
                  onNavigate={onClose}
                />
              ) : (
                <ModuleRailItem
                  modulo={modulo}
                  pathname={pathname}
                  onNavigate={onClose}
                />
              )
            ) : (
              <TopLevelSecao
                modulo={modulo}
                pathname={pathname}
                abertoState={abertoState}
                toggle={toggleTopLevel}
                onNavigate={onClose}
              />
            )}
          </React.Fragment>
        ))}
      </ScrollArea>

      {!collapsed && unidadeDaSessao && (
        <SidebarFooter>
          <UnitTag title={unidadeDaSessao}>Unidade · {unidadeDaSessao}</UnitTag>
        </SidebarFooter>
      )}
    </>
  );
}

export default function Sidebar({
  collapsed,
  onToggleCollapsed,
  canToggle = true,
  mobileOpen,
  onCloseMobile,
}) {
  const location = useLocation();
  const { hasAllPermissions, hasAnyPermission, isMaster, temProduto, temModulo } = useAuth();
  // Chave da Central de Projetos e telas antigas escondidas (ADR-0089).
  const estadoCentral = useEstadoCentral();

  // Bugfix #11 fix v3 (2026-05-29): listener global de mousemove que
  // armazena a posicao atual em window.__sidebarMousePos. Consumido
  // pelo scheduleClose dos ModuleRailItem para checar safe zone antes
  // de fechar o flyout. 1 listener so para o app inteiro (passivo,
  // custo zero). Vive enquanto a Sidebar estiver montada.
  useEffect(() => {
    const onMove = (e) => {
      window.__sidebarMousePos = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // O gate de cada item (contrato, Master-only, condição da Central e
  // permissões) mora em `utils/modules.jsx` (`itemVisivel`): a Sidebar, os
  // favoritos e os atalhos decidem pela mesma regra.
  const modulosDoMenu = useMemo(
    () => filtrarModulosDoMenu({ hasAllPermissions, hasAnyPermission, isMaster, temProduto, temModulo, estadoCentral }),
    [hasAllPermissions, hasAnyPermission, isMaster, temProduto, temModulo, estadoCentral]
  );

  // Estado de accordions top-level (persiste em localStorage por moduloId).
  // moduloAtivoId garante que ao navegar para nova rota, a categoria abre.
  const moduloAtivoId = moduloDaRota(location.pathname);
  const todosIds = useMemo(() => modulosDoMenu.map((m) => m.id), [modulosDoMenu]);
  const { estado: abertoState, toggle: toggleTopLevel } = useTopLevelAccordion(
    moduloAtivoId,
    todosIds
  );

  return (
    <>
      <SidebarRoot $collapsed={collapsed} aria-label="Navegacao principal" data-tour="menu-lateral">
        <SidebarContent
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          canToggle={canToggle}
          onClose={undefined}
          modulosVisiveis={modulosDoMenu}
          pathname={location.pathname}
          isMobile={false}
          abertoState={abertoState}
          toggleTopLevel={toggleTopLevel}
        />
      </SidebarRoot>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <SidebarOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onCloseMobile}
            />
            <SidebarDrawer
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              aria-label="Navegacao principal (mobile)"
              data-tour="menu-lateral"
            >
              <SidebarContent
                collapsed={false}
                onToggleCollapsed={undefined}
                onClose={onCloseMobile}
                modulosVisiveis={modulosDoMenu}
                pathname={location.pathname}
                isMobile
                abertoState={abertoState}
                toggleTopLevel={toggleTopLevel}
              />
            </SidebarDrawer>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
